import React, { useState, useEffect } from 'react';
import { 
  Upload, X, CheckCircle, ArrowLeft
} from 'lucide-react';
import {
  uploadPulseMedia,
  createPulsePost
} from '../utils/pulseAdminApi';
import { supabase } from '../utils/supabaseClient';
import '../styles/CreatePulsePost.css';

const CreatePulsePost = ({ onBack, onSuccess }) => {
  // Get current user from Supabase auth
  const [currentUserId, setCurrentUserId] = useState(null);

  // Create post form
  const [newPost, setNewPost] = useState({
    text_content: '',
    source: '',
    media_type: 'image',
    media_source: 'upload', // Must be 'upload' or 'portfolio' per DB constraint
    status: 'Public',
    post_type: 'artwork', // Default to artwork (lowercase per DB constraint)
    target_type: 'artwork',
    target_url: '',
    user_id: '',
    vc_id: null
  });

  // Get logged-in user on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        setNewPost(prev => ({ ...prev, user_id: user.id }));
      }
    };
    getCurrentUser();
  }, []);
  
  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadingFile(true);
      setUploadProgress(0);

      // Determine media type from file
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';

      // Simulate progress for user feedback (Cloudinary doesn't provide real progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Upload to Cloudinary
      const uploadResult = await uploadPulseMedia(file, mediaType);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Update form with uploaded URL
      setNewPost(prev => ({
        ...prev,
        source: uploadResult.url,
        media_type: mediaType
      }));

      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);

    } catch (error) {
      console.error('Upload failed:', error);
      setError('Upload failed: ' + error.message);
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!newPost.source) {
      setError('Please add media (URL or upload a file)');
      return;
    }
    if (!newPost.user_id) {
      setError('User not authenticated');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await createPulsePost(newPost);
      
      // Success - call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
      
      // Reset form
      resetForm();
      
    } catch (error) {
      console.error('Error creating post:', error);
      setError(error.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setNewPost({
      text_content: '',
      source: '',
      media_type: 'image',
      media_source: 'upload', // Must be 'upload' or 'portfolio' per DB constraint
      status: 'Public',
      post_type: 'artwork',
      target_type: 'artwork',
      target_url: '',
      user_id: currentUserId || '',
      vc_id: null
    });
  };

  return (
    <div className="create-pulse-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={20} />
          Back to Pulse Admin
        </button>
        <h1>Create Pulse Post</h1>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <X size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Form */}
      <div className="create-form">
        {/* Media Selection */}
        <div className="form-section">
          <h3>1. Add Media</h3>
          <div className="form-group">
            <label>Media Source</label>
            <div className="media-source-options">
              <label className={newPost.media_source === 'upload' ? 'active' : ''}>
                <input
                  type="radio"
                  value="upload"
                  checked={newPost.media_source === 'upload'}
                  onChange={(e) => setNewPost(prev => ({ ...prev, media_source: e.target.value }))}
                />
                Upload File or Paste URL
              </label>
            </div>

            <input
              type="text"
              placeholder="Paste URL or click below to upload..."
              value={newPost.source}
              onChange={(e) => setNewPost(prev => ({ ...prev, source: e.target.value }))}
              className="url-input"
            />
            
            <div className="upload-section">
              <input
                type="file"
                id="pulse-file-upload"
                accept="image/*,video/*"
                onChange={handleFileUpload}
                disabled={uploadingFile}
                style={{ display: 'none' }}
              />
              <label htmlFor="pulse-file-upload" className="upload-btn">
                <Upload size={20} />
                {uploadingFile ? 'Uploading...' : 'Or Choose File to Upload'}
              </label>
              {uploadingFile && (
                <div className="upload-progress">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  <span>{uploadProgress}%</span>
                </div>
              )}
              {newPost.source && !uploadingFile && newPost.media_source === 'upload' && (
                <div className="uploaded-preview">
                  <CheckCircle size={16} />
                  <span>Media ready</span>
                </div>
              )}
            </div>
          </div>

          {/* Media Preview */}
          {newPost.source && (
            <div className="form-group">
              <label>Preview</label>
              <div className="media-preview">
                {newPost.media_type === 'video' ? (
                  <video src={newPost.source} controls />
                ) : (
                  <img src={newPost.source} alt="Preview" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content & Settings */}
        <div className="form-section">
          <h3>2. Add Details</h3>
          
          {/* Text Content */}
          <div className="form-group">
            <label>Caption</label>
            <textarea
              rows={4}
              placeholder="Write a caption..."
              value={newPost.text_content}
              onChange={(e) => setNewPost(prev => ({ ...prev, text_content: e.target.value }))}
            />
          </div>

          {/* Post Settings */}
          <div className="form-row">
            <div className="form-group">
              <label>Media Type</label>
              <select
                value={newPost.media_type}
                onChange={(e) => setNewPost(prev => ({ ...prev, media_type: e.target.value }))}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>

            <div className="form-group">
              <label>Post Type</label>
              <select
                value={newPost.post_type}
                onChange={(e) => setNewPost(prev => ({ ...prev, post_type: e.target.value }))}
              >
                <option value="artwork">Artwork</option>
                <option value="collection">Collection</option>
                <option value="event">Event</option>
                <option value="gallery">Gallery</option>
                <option value="class">Class</option>
                <option value="milestone">Milestone</option>
                <option value="inspiration">Inspiration</option>
                <option value="personal">Personal</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={newPost.status}
                onChange={(e) => setNewPost(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="Public">Public</option>
                <option value="Private">Private</option>
              </select>
            </div>
          </div>

          {/* Target URL (optional) */}
          <div className="form-group">
            <label>Target URL (optional)</label>
            <input
              type="text"
              placeholder="e.g., /artwork/123 or /event/456"
              value={newPost.target_url}
              onChange={(e) => setNewPost(prev => ({ ...prev, target_url: e.target.value }))}
            />
            <small>Link this post to a specific artwork or event</small>
          </div>
        </div>

        {/* Submit Actions */}
        <div className="form-actions">
          <button 
            className="btn-secondary" 
            onClick={onBack}
            disabled={submitting}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSubmit}
            disabled={submitting || !newPost.source || !newPost.user_id}
          >
            {submitting ? 'Creating...' : 'Create Pulse Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePulsePost;
